import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowUpRight, Newspaper, RefreshCw, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { fetchPublishedUpdates, formatUpdateDate, newsSite, readUpdatesSeen, updatesSeenKey, UPDATES_TIMEOUT_MS } from '../../lib/productUpdates.js';
import '../../styles/product-updates.css';

// Keyed by user in Sidebar so a different account gets its own seen receipt,
// and an old request cannot update the next account's navigation state.
export default function ProductUpdates({ userId, onMobileClose, mobileMenuButtonRef }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [request, setRequest] = useState(0);
  const [feed, setFeed] = useState({ status: 'loading', posts: [] });
  const [seen, setSeen] = useState(() => readUpdatesSeen(userId));
  const closeRef = useRef(null);
  const site = newsSite(api.baseURL);
  const latest = feed.posts[0];
  const unread = Boolean(latest && latest.revision > seen);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), UPDATES_TIMEOUT_MS);
    setFeed({ status: 'loading', posts: [] });
    fetchPublishedUpdates(api.baseURL, controller.signal)
      .then(posts => { if (active) setFeed({ status: 'ready', posts }); })
      .catch(() => { if (active) setFeed({ status: 'error', posts: [] }); })
      .finally(() => clearTimeout(timer));
    return () => { active = false; controller.abort(); clearTimeout(timer); };
  }, [request]);

  useEffect(() => {
    // Opening the menu alone never marks unread news as seen. Only a successful
    // fresh panel read does; failures/empty responses do not consume a receipt.
    if (!open || feed.status !== 'ready' || !latest) return;
    setSeen(value => Math.max(value, latest.revision));
    try { window.localStorage.setItem(updatesSeenKey(userId), String(Math.max(seen, latest.revision))); }
    catch { /* Reading updates still works with storage blocked. */ }
  }, [open, feed, userId]);

  const changeOpen = next => {
    if (next) {
      onMobileClose?.({ restoreFocus: false });
      // Revalidate on every open; no static fallback may resurrect a withdrawn story.
      setFeed({ status: 'loading', posts: [] });
      setRequest(value => value + 1);
    }
    setOpen(next);
  };

  return (
    <Dialog.Root open={open} onOpenChange={changeOpen}>
      <Dialog.Trigger asChild>
        <button type="button" className="sb-link sb-link-sm pu-trigger" title={t('productUpdates.title')}
          aria-label={`${t('productUpdates.title')}${unread ? ` — ${t('productUpdates.unread')}` : ''}`}>
          <span className="gi"><Newspaper size={19} aria-hidden="true" />{unread && <i className="pu-dot" aria-hidden="true" />}</span>
          <span className="pu-trigger-copy">{t('productUpdates.title')}
            {latest && <time dateTime={latest.dateTime}>{formatUpdateDate(latest.revision, i18n.language, true)}</time>}
          </span>
        </button>
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="pu-overlay" />
        <Dialog.Content className="pu-panel"
          onOpenAutoFocus={event => { event.preventDefault(); closeRef.current?.focus(); }}
          onCloseAutoFocus={event => {
            if (window.matchMedia?.('(max-width: 768px)').matches && mobileMenuButtonRef?.current) {
              event.preventDefault(); mobileMenuButtonRef.current.focus();
            }
          }}>
          <header className="pu-header">
            <div className="pu-heading-icon" aria-hidden="true"><Newspaper size={23} /></div>
            <Dialog.Title className="pu-title">{t('productUpdates.heading')}</Dialog.Title>
            <Dialog.Description className="pu-intro">{t('productUpdates.description')}</Dialog.Description>
            <Dialog.Close asChild><button ref={closeRef} type="button" className="pu-close" aria-label={t('common.close')}><X size={20} aria-hidden="true" /></button></Dialog.Close>
          </header>
          <div className="pu-scroll" aria-busy={feed.status === 'loading'}>
            {feed.status === 'loading' && <div className="pu-state" role="status"><RefreshCw className="pu-loading" size={22} aria-hidden="true" /><p>{t('productUpdates.loading')}</p></div>}
            {feed.status === 'error' && <div className="pu-state" role="status"><p>{t('productUpdates.error')}</p><button type="button" className="pu-action" onClick={() => setRequest(value => value + 1)}><RefreshCw size={15} aria-hidden="true" />{t('productUpdates.retry')}</button></div>}
            {feed.status === 'ready' && !feed.posts.length && <div className="pu-state" role="status"><Newspaper size={28} aria-hidden="true" /><h3>{t('productUpdates.emptyTitle')}</h3><p>{t('productUpdates.emptyDescription')}</p></div>}
            {feed.status === 'ready' && feed.posts.map((post, index) => {
              const expired = post.type === 'offer' && (post.offerExpired || !post.expiresAt || post.expiresAt <= Date.now());
              return <article key={post.slug} className={`pu-story${index === 0 ? ' pu-latest' : ''}`}>
                <div className="pu-meta"><span className={`pu-tag${expired ? ' is-expired' : ''}`}>{t(`productUpdates.${expired ? 'expired' : post.type}`)}</span><time dateTime={post.dateTime}>{formatUpdateDate(post.revision, i18n.language)}</time></div>
                <h3>{post.title}</h3>
                {post.excerpt && <p>{post.excerpt}</p>}
                {site && <a className="pu-read" href={`${site}/updates/${post.slug}`} target="_blank" rel="noopener noreferrer" aria-label={`${t('productUpdates.read')}: ${post.title} (${t('productUpdates.newTab')})`}>{t('productUpdates.read')}<ArrowUpRight size={15} aria-hidden="true" /></a>}
              </article>;
            })}
          </div>
          <footer className="pu-footer"><span>{t('productUpdates.footer')}</span>{site && <a className="pu-action" href={`${site}/updates`} target="_blank" rel="noopener noreferrer" aria-label={`${t('productUpdates.all')} (${t('productUpdates.newTab')})`}>{t('productUpdates.all')}<ArrowUpRight size={15} aria-hidden="true" /></a>}</footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
